import React from "react";

/**
 * Horizontal setting row: label on the left, control aligned to the right.
 * Shared between the global Design panel and the per-field settings panel so
 * both look consistent.
 */
export const SettingRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <s-stack
    direction="inline"
    gap="base"
    alignItems="center"
    justifyContent="space-between"
  >
    <s-text type="strong">{label}</s-text>
    {children}
  </s-stack>
);

/**
 * Color control rendered with Polaris' native color field.
 *
 * When 'onClear' is provided, a tertiary button is shown while the value is
 * explicitly set (an override). Clearing lets the field fall back to the
 * inherited/global color again.
 */
export const ColorRow: React.FC<{
  label: string;
  value?: string;
  fallback: string;
  name?: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  clearLabel?: string;
}> = ({
  label,
  value,
  fallback,
  name,
  onChange,
  onClear,
  clearLabel = "Restablecer al estilo global",
}) => (
  <s-stack direction="block" gap="small-200">
    <s-color-field
      label={label}
      name={name}
      value={value ?? fallback}
      onInput={(event) => {
        // Read the value synchronously before dispatching: the store update may
        // defer work and `event.currentTarget` is nulled after the handler runs.
        const nextValue = event.currentTarget.value;
        onChange(nextValue);
      }}
    />
    {onClear && value ? (
      <s-button variant="tertiary" onClick={onClear}>
        {clearLabel}
      </s-button>
    ) : null}
  </s-stack>
);

function rangeFillStyle(
  value: number,
  min: number,
  max: number
): React.CSSProperties {
  const pct =
    max === min
      ? 0
      : Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  return {
    background: `linear-gradient(to right, #00E5C4 0%, #00E5C4 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`,
  };
}

export const SliderRow: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  layout?: "inline" | "stacked";
  formatValue?: (value: number) => string;
}> = ({
  label,
  value,
  min,
  max,
  step = 1,
  unit = "px",
  onChange,
  layout = "inline",
  formatValue,
}) => {
  const safeValue = value ?? min;
  const displayValue = formatValue
    ? formatValue(safeValue)
    : `${safeValue}${unit}`;

  const rangeInput = (
    <input
      type="range"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={safeValue}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className={
        layout === "stacked" ? "codway-range w-full" : "codway-range flex-1"
      }
      style={rangeFillStyle(safeValue, min, max)}
    />
  );

  if (layout === "stacked") {
    return (
      <s-stack direction="block" gap="small-200">
        <s-stack
          direction="inline"
          alignItems="center"
          justifyContent="space-between"
          inlineSize="100%"
        >
          <s-text type="strong" color="subdued">
            {label}
          </s-text>
          <s-text color="subdued">{displayValue}</s-text>
        </s-stack>
        {rangeInput}
      </s-stack>
    );
  }

  return (
    <SettingRow label={label}>
      <div className="flex w-[60%] shrink-0 items-center gap-2">
        {rangeInput}
        <span className="w-14 shrink-0 rounded-lg border border-gray-200 px-2 py-1.5 text-center text-sm font-medium text-gray-700 tabular-nums">
          {displayValue}
        </span>
      </div>
    </SettingRow>
  );
};
